import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  useTheme,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  TextField,
} from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import BusinessIcon from "@mui/icons-material/Business";
import CategoryIcon from "@mui/icons-material/Category";
import ShieldIcon from "@mui/icons-material/Shield";
import PersonIcon from "@mui/icons-material/Person";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { GET_SYSTEMS, GET_ASSESSMENT, UPDATE_SYSTEM_CONFIGS, START_ACCESS_REVIEW_ASSESSMENT } from "../../../../../apis/settingsApis/access-apis/accessApis";
import { GET_STAFF_DETAILS } from "../../../../../apis/organization-apis/people-apis/peopleApis";
import Role from "../../../../../utils/roles";
import { AssessmentStatus } from "../../../../../utils/enums";
import UserManagementTab from "../UserManagement/UserManagement";
import AccessSystemReview from "../../Reviews/AccessSystemReview";

const SystemDetails = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { systemId } = useParams();
  const [tab, setTab] = useState("0");
  const [editingSystemAdmin, setEditingSystemAdmin] = useState(false);
  const [editingIsCritical, setEditingIsCritical] = useState(false);
  const [editingReviewCycle, setEditingReviewCycle] = useState(false);
  const [tempSystemAdmin, setTempSystemAdmin] = useState("");
  const [tempIsCritical, setTempIsCritical] = useState(false);
  const [tempReviewCycle, setTempReviewCycle] = useState("");

  const { data: staffData } = useQuery(GET_STAFF_DETAILS);
  const employees = staffData?.getEmployees?.data || [];

  const admins = useMemo(() => {
    return employees
      .filter(emp => {
        return emp.roles?.some(role => role?.role_description?.toLowerCase() === Role.ADMIN.toLowerCase());
      })
      .map(emp => {
        const primaryEmail = emp.emails?.find((em) => em.type === "work")?.email || emp.emails?.[0]?.email || "";
        return {
          email: primaryEmail,
          name: emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
        };
      })
      .filter(admin => admin.email);
  }, [employees]);

  const { data: systemsData, loading: systemLoading, refetch: refetchSystem } = useQuery(GET_SYSTEMS, {
    variables: {
      page: 1,
      limit: 1,
      filter: { id: [parseInt(systemId)] },
    },
    skip: !systemId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const [updateSystemConfigs, { loading: updating }] = useMutation(UPDATE_SYSTEM_CONFIGS);
  const [startAccessReviewAssessment, { loading: startingReview }] = useMutation(START_ACCESS_REVIEW_ASSESSMENT);

  const { data: assessmentsData, refetch: refetchAssessments } = useQuery(GET_ASSESSMENT, {
    variables: {
      page: 1,
      limit: 1000,
      filter: { resource_id: parseInt(systemId) },
    },
    skip: !systemId,
  });

  const currentSystem = systemsData?.getSystems?.data?.[0];
  const allAssessments = assessmentsData?.getAssessment?.data || [];

  const draftAssessment = allAssessments.find((a) => a.status_title === AssessmentStatus.DRAFT);

  useEffect(() => {
    if (editingSystemAdmin) {
      setEditingSystemAdmin(false);
      setTempSystemAdmin("");
    }
    if (editingIsCritical) {
      setEditingIsCritical(false);
      setTempIsCritical(false);
    }
    if (editingReviewCycle) {
      setEditingReviewCycle(false);
      setTempReviewCycle("");
    }
  }, [systemId]);

  if (systemLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!currentSystem) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/access")}
          sx={{
            mb: 2,
            color: theme.customColors?.text?.secondary,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Back to Overview
        </Button>
        <Typography variant="h5" color="error">
          System not found
        </Typography>
      </Box>
    );
  }

  const handleStartReview = async () => {
    if (draftAssessment) {
      setTab("1");
      return;
    }

    if (!currentSystem) return;

    try {
      const systemAssessments = allAssessments || [];
      const nextNumber = systemAssessments.length + 1;
      const { data } = await startAccessReviewAssessment({
        variables: {
          assesement_data: {
            title: `Access Review - ${nextNumber}`,
            description: `Access review for ${currentSystem?.label || currentSystem?.name || "system"}`,
            resource_id: parseInt(systemId, 10),
          },
        },
      });

      const success = data?.startAccessReviewAssesement?.success;
      if (success) {
        await refetchAssessments();
        setTab("1");
      } else {
        console.error("Failed to start access review:", data?.startAccessReviewAssesement?.message);
      }
    } catch (error) {
      console.error("Failed to start access review:", error);
    }
  };

  const handleEditSystemAdmin = () => {
    setTempSystemAdmin(currentSystem?.system_admin || "");
    setEditingSystemAdmin(true);
  };

  const handleSaveSystemAdmin = async () => {
    try {
      const result = await updateSystemConfigs({
        variables: {
          input: [{
            id: parseInt(systemId),
            system_admin: tempSystemAdmin
          }]
        },
      });

      const responseData = result?.data?.updateSystemConfigs?.data;
      const newSystemId = responseData?.[0]?.id;

      if (newSystemId && newSystemId !== parseInt(systemId)) {
        navigate(`/admin/access/system/${newSystemId}`, { replace: true });
      } else {
        await refetchSystem();
      }

      setEditingSystemAdmin(false);
    } catch (error) {
      console.error("Failed to update account owner:", error);
      setEditingSystemAdmin(false);
    }
  };

  const handleCancelSystemAdmin = () => {
    setEditingSystemAdmin(false);
    setTempSystemAdmin("");
  };

  const handleEditIsCritical = () => {
    setTempIsCritical(currentSystem?.is_critical);
    setEditingIsCritical(true);
  };

  const handleSaveIsCritical = async () => {
    try {
      const result = await updateSystemConfigs({
        variables: {
          input: [{
            id: parseInt(systemId),
            is_critical: tempIsCritical
          }]
        },
      });

      const responseData = result?.data?.updateSystemConfigs?.data;
      const newSystemId = responseData?.[0]?.id;

      if (newSystemId && newSystemId !== parseInt(systemId)) {
        navigate(`/admin/access/system/${newSystemId}`, { replace: true });
      } else {
        await refetchSystem();
      }

      setEditingIsCritical(false);
    } catch (error) {
      console.error("Failed to update critical status:", error);
      setEditingIsCritical(false);
    }
  };

  const handleCancelIsCritical = () => {
    setEditingIsCritical(false);
    setTempIsCritical(false);
  };

  const handleEditReviewCycle = () => {
    setTempReviewCycle(currentSystem?.review_cycle?.toString() || "");
    setEditingReviewCycle(true);
  };

  const handleSaveReviewCycle = async () => {
    try {
      const reviewCycleValue = tempReviewCycle ? parseInt(tempReviewCycle, 10) : null;
      const result = await updateSystemConfigs({
        variables: {
          input: [{
            id: parseInt(systemId),
            review_cycle: reviewCycleValue
          }]
        },
      });

      const responseData = result?.data?.updateSystemConfigs?.data;
      const newSystemId = responseData?.[0]?.id;

      if (newSystemId && newSystemId !== parseInt(systemId)) {
        navigate(`/admin/access/system/${newSystemId}`, { replace: true });
      } else {
        await refetchSystem();
      }

      setEditingReviewCycle(false);
    } catch (error) {
      console.error("Failed to update review cycle:", error);
      setEditingReviewCycle(false);
    }
  };

  const handleCancelReviewCycle = () => {
    setEditingReviewCycle(false);
    setTempReviewCycle("");
  };

  const getCriticalColor = (isCritical) => {
    if (isCritical === true) return "#DC2626";
    if (isCritical === false) return "#10B981";
    return "#6B7280"; // Grey for null/undefined
  };

  return (
    <>
      <Box sx={{ padding: { xs: 2 }, minHeight: "100vh" }}>
        {/* Back Button */}
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/admin/access")}
            sx={{
              color: theme.customColors?.text?.secondary,
              textTransform: "none",
              fontWeight: 500,
              "&:hover": { bgcolor: theme.customColors?.background?.light },
            }}
          >
            Back to Overview
          </Button>
        </Box>

        {/* System Details Card */}
        <Card
          sx={{
            mb: 3,
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid #E8EAED",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Typography variant="h4" fontWeight="600" color={theme.customColors?.text?.strong}>
                {currentSystem?.label}
              </Typography>
              {currentSystem?.account_label && (
                <Chip
                  label={currentSystem.account_label}
                  size="small"
                  sx={{
                    bgcolor: '#E5F7FF',
                    color: '#0277BD',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 28,
                  }}
                />
              )}
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={2.4}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <CategoryIcon sx={{ fontSize: 18, color: theme.customColors?.text?.secondary }} />
                  <Typography variant="caption" color={theme.customColors?.text?.secondary}>
                    Category
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="600">
                  {currentSystem?.category || "-"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <BusinessIcon sx={{ fontSize: 18, color: theme.customColors?.text?.secondary }} />
                  <Typography variant="caption" color={theme.customColors?.text?.secondary}>
                    Type
                  </Typography>
                </Box>
                <Chip
                  label={(currentSystem?.integration_type || '').toUpperCase() === 'MANUAL' ? 'Manual' : 'Automated'}
                  size="small"
                  sx={{
                    bgcolor: (currentSystem?.integration_type || '').toUpperCase() === 'MANUAL' ? "#FEF3C7" : "#DBEAFE",
                    color: (currentSystem?.integration_type || '').toUpperCase() === 'MANUAL' ? "#92400E" : "#1E40AF",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 18, color: theme.customColors?.text?.secondary }} />
                  <Typography variant="caption" color={theme.customColors?.text?.secondary}>
                    Account Owner
                  </Typography>
                  {!editingSystemAdmin && (
                    <Tooltip title={!currentSystem?.is_review_active ? "Can't update in archived system" : "Edit account owner"}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleEditSystemAdmin}
                          disabled={!currentSystem?.is_review_active}
                          sx={{
                            p: 0.5,
                            ml: 0.5,
                            color: theme.customColors?.text?.secondary,
                            "&:hover": { bgcolor: theme.customColors?.background?.light },
                            "&.Mui-disabled": {
                              color: theme.customColors?.text?.disabled || "#D1D5DB",
                            },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                {editingSystemAdmin ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={tempSystemAdmin || ""}
                        onChange={(e) => setTempSystemAdmin(e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <Typography sx={{ color: '#9CA3AF', fontSize: '0.875rem' }}>Select admin...</Typography>;
                          }
                          const admin = admins.find(a => a.email === selected);
                          return admin ? admin.name : selected;
                        }}
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.customColors?.border?.main,
                          },
                        }}
                      >
                        <MenuItem value="" sx={{ fontSize: '0.875rem', py: 1, color: '#9CA3AF', fontStyle: 'italic' }}>
                          None (Unassigned)
                        </MenuItem>
                        {admins.map((admin) => (
                          <MenuItem key={admin.email} value={admin.email} sx={{ fontSize: '0.875rem', py: 1 }}>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>{admin.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{admin.email}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Tooltip title="Save">
                      <IconButton
                        size="small"
                        onClick={handleSaveSystemAdmin}
                        disabled={updating}
                        sx={{
                          color: "#10B981",
                          "&:hover": { bgcolor: "rgba(16, 185, 129, 0.1)" },
                        }}
                      >
                        <SaveIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton
                        size="small"
                        onClick={handleCancelSystemAdmin}
                        sx={{
                          color: theme.customColors?.text?.secondary,
                          "&:hover": { bgcolor: theme.customColors?.background?.light },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Typography variant="body2" fontWeight="600">
                    {(() => {
                      const admin = admins.find(a => a.email === currentSystem?.system_admin);
                      return admin ? admin.name : currentSystem?.system_admin || "-";
                    })()}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <ShieldIcon sx={{ fontSize: 18, color: theme.customColors?.text?.secondary }} />
                  <Typography variant="caption" color={theme.customColors?.text?.secondary}>
                    Critical Account
                  </Typography>
                  {!editingIsCritical && (
                    <Tooltip
                      title={
                        !currentSystem?.is_review_active
                          ? "Can't update in archived system"
                          : "Edit critical status"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleEditIsCritical}
                          disabled={!currentSystem?.is_review_active}
                          sx={{
                            p: 0.5,
                            ml: 0.5,
                            color: theme.customColors?.text?.secondary,
                            "&:hover": { bgcolor: theme.customColors?.background?.light },
                            "&.Mui-disabled": {
                              color: theme.customColors?.text?.disabled || "#D1D5DB",
                            },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                {editingIsCritical ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={tempIsCritical === true ? 'critical' : tempIsCritical === false ? 'not_critical' : ''}
                        onChange={(e) => setTempIsCritical(e.target.value === 'critical' ? true : e.target.value === 'not_critical' ? false : undefined)}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <Typography sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>Set</Typography>;
                          }
                          return selected === 'critical' ? 'Critical' : 'Not Critical';
                        }}
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.customColors?.border?.main,
                          },
                        }}
                      >
                        <MenuItem value="not_critical">Not Critical</MenuItem>
                        <MenuItem value="critical">Critical</MenuItem>
                      </Select>
                    </FormControl>
                    <Tooltip title="Save">
                      <IconButton
                        size="small"
                        onClick={handleSaveIsCritical}
                        disabled={updating}
                        sx={{
                          color: "#10B981",
                          "&:hover": { bgcolor: "rgba(16, 185, 129, 0.1)" },
                        }}
                      >
                        <SaveIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton
                        size="small"
                        onClick={handleCancelIsCritical}
                        sx={{
                          color: theme.customColors?.text?.secondary,
                          "&:hover": { bgcolor: theme.customColors?.background?.light },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Chip
                    label={currentSystem?.is_critical === true ? "Critical" : currentSystem?.is_critical === false ? "Not Critical" : "Select critical status"}
                    size="small"
                    sx={{
                      bgcolor: getCriticalColor(currentSystem?.is_critical) + "20",
                      color: getCriticalColor(currentSystem?.is_critical),
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <ScheduleIcon sx={{ fontSize: 18, color: theme.customColors?.text?.secondary }} />
                  <Typography variant="caption" color={theme.customColors?.text?.secondary}>
                    Review Cycle
                  </Typography>
                  {!editingReviewCycle && (
                    <Tooltip title={!currentSystem?.is_review_active ? "Can't update in archived system" : "Edit review cycle"}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleEditReviewCycle}
                          disabled={!currentSystem?.is_review_active}
                          sx={{
                            p: 0.5,
                            ml: 0.5,
                            color: theme.customColors?.text?.secondary,
                            "&:hover": { bgcolor: theme.customColors?.background?.light },
                            "&.Mui-disabled": {
                              color: theme.customColors?.text?.disabled || "#D1D5DB",
                            },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                {editingReviewCycle ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <TextField
                      type="number"
                      size="small"
                      value={tempReviewCycle}
                      onChange={(e) => setTempReviewCycle(e.target.value)}
                      placeholder="Days"
                      inputProps={{ min: 1, max: 365 }}
                      sx={{
                        width: "120px",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: theme.customColors?.border?.main,
                          },
                        },
                      }}
                    />
                    <Typography variant="body2" sx={{ color: theme.customColors?.text?.secondary }}>
                      days
                    </Typography>
                    <Tooltip title="Save">
                      <IconButton
                        size="small"
                        onClick={handleSaveReviewCycle}
                        disabled={updating}
                        sx={{
                          color: "#10B981",
                          "&:hover": { bgcolor: "rgba(16, 185, 129, 0.1)" },
                        }}
                      >
                        <SaveIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton
                        size="small"
                        onClick={handleCancelReviewCycle}
                        sx={{
                          color: theme.customColors?.text?.secondary,
                          "&:hover": { bgcolor: theme.customColors?.background?.light },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Typography variant="body2" fontWeight="600">
                    {currentSystem?.review_cycle ? `${currentSystem.review_cycle} days` : "-"}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <TabContext value={tab}>
          {/* Tab List */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: theme.customColors?.border?.main,
              mb: 2,
            }}
          >
            <TabList
              onChange={(_, v) => setTab(v)}
              aria-label="System Management Tabs"
            >
              <Tab label="User Management" value="0" />
              <Tab label="Access Reviews" value="1" />
            </TabList>
          </Box>

          {/* Tab Panels */}
          <TabPanel value="0" sx={{ p: 0 }}>
            <UserManagementTab
              systemId={systemId}
              systemData={currentSystem}
              onStartReview={handleStartReview}
              startReviewLoading={startingReview}
              hasDraft={!!draftAssessment}
            />
          </TabPanel>
          <TabPanel value="1" sx={{ p: 0 }}>
            <AccessSystemReview
              systemId={systemId}
              isEmbedded={true}
            />
          </TabPanel>
        </TabContext>
      </Box>
    </>
  );
};

export default SystemDetails;